import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { formatDistanceToNow } from "date-fns";
import { da } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MapPin, Users, RefreshCw, Mail, Phone, Tag, ExternalLink, Flame, Sparkles } from "lucide-react";
import {
  getLiveVisitorSessions,
  getTopVisitorCities,
  listMarketingContacts,
  getCityHeatmap,
} from "@/lib/visitor-tracking.functions";
import { LazyCustomerMap } from "@/components/admin/LazyCustomerMap";

export const Route = createFileRoute("/admin/customer-map")({
  head: () => ({
    meta: [
      { title: "Customer Map - AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CustomerMapPage,
});

type Period = 7 | 30 | 90;

// Stable fake sessions covering all four path categories — used by demo-mode
// to verify color coding, label-suppression (>20 sessions) and cluster-markers
// (multiple sessions at the same coordinate). Coordinates are real Danish
// cities so FitBounds behaves like production.
//
// Layout:
//   - 28 total sessions (above the 20-session label-suppression threshold)
//   - 1 cluster of 3 in København (same coord, mixed paths)
//   - 1 cluster of 2 in Aarhus (same coord, both /biler-til-salg)
//   - 23 spread across other cities, mix of all 4 categories
type DemoSession = {
  session_id: string;
  city: string;
  country: string;
  current_path: string;
  last_seen_at: string;
  latitude: number;
  longitude: number;
};

function makeDemoSession(
  id: string,
  city: string,
  lat: number,
  lng: number,
  path: string,
  ageSeconds: number,
): DemoSession {
  return {
    session_id: id,
    city,
    country: "DK",
    current_path: path,
    last_seen_at: new Date(Date.now() - ageSeconds * 1000).toISOString(),
    latitude: lat,
    longitude: lng,
  };
}

const DEMO_SESSIONS: DemoSession[] = [
  // Cluster 1 — København, 3 sessions on identical coord
  makeDemoSession("demo-cph-1", "København", 55.6761, 12.5683, "/biler-til-salg/bmw-x5-2022", 12),
  makeDemoSession("demo-cph-2", "København", 55.6761, 12.5683, "/blog/elbil-guide-2026", 35),
  makeDemoSession("demo-cph-3", "København", 55.6761, 12.5683, "/kontakt", 8),
  // Cluster 2 — Aarhus, 2 sessions on identical coord
  makeDemoSession("demo-aar-1", "Aarhus", 56.1629, 10.2039, "/biler-til-salg", 45),
  makeDemoSession("demo-aar-2", "Aarhus", 56.1629, 10.2039, "/biler-til-salg/audi-a4", 22),
  // Spread across DK — biler (gold)
  makeDemoSession("demo-cars-1", "Odense", 55.4038, 10.4024, "/biler-til-salg/vw-golf", 60),
  makeDemoSession("demo-cars-2", "Aalborg", 57.0488, 9.9217, "/biler-til-salg/tesla-model-3", 18),
  makeDemoSession("demo-cars-3", "Esbjerg", 55.4765, 8.4594, "/biler-til-salg", 120),
  makeDemoSession("demo-cars-4", "Randers", 56.4607, 10.0369, "/biler-til-salg/mercedes-c220", 4),
  makeDemoSession("demo-cars-5", "Kolding", 55.4904, 9.4722, "/biler-til-salg", 75),
  makeDemoSession("demo-cars-6", "Vejle", 55.7099, 9.5357, "/biler-til-salg/bmw-3-series", 30),
  makeDemoSession("demo-cars-7", "Horsens", 55.8607, 9.8503, "/biler-til-salg", 95),
  makeDemoSession("demo-cars-8", "Helsingør", 56.0361, 12.6136, "/biler-til-salg/peugeot-3008", 50),
  // Blog (blue)
  makeDemoSession("demo-blog-1", "Roskilde", 55.6415, 12.0803, "/blog/koeb-brugt-bil-tjekliste", 110),
  makeDemoSession("demo-blog-2", "Silkeborg", 56.1697, 9.5453, "/blog/elbil-guide-2026", 65),
  makeDemoSession("demo-blog-3", "Næstved", 55.2297, 11.7607, "/blog/leasing-vs-koeb", 40),
  makeDemoSession("demo-blog-4", "Frederikshavn", 57.4407, 10.5464, "/blog", 200),
  // Kontakt (green)
  makeDemoSession("demo-contact-1", "Herning", 56.1397, 8.9786, "/kontakt", 15),
  makeDemoSession("demo-contact-2", "Holstebro", 56.3608, 8.6164, "/kontakt", 28),
  makeDemoSession("demo-contact-3", "Hillerød", 55.9329, 12.3057, "/kontakt", 6),
  makeDemoSession("demo-contact-4", "Viborg", 56.4521, 9.4019, "/kontakt", 80),
  // Andet (default/dark)
  makeDemoSession("demo-other-1", "Slagelse", 55.4040, 11.3551, "/om-os", 180),
  makeDemoSession("demo-other-2", "Svendborg", 55.0588, 10.6098, "/handelsbetingelser", 240),
  makeDemoSession("demo-other-3", "Hjørring", 57.4626, 9.9819, "/privatliv", 300),
  makeDemoSession("demo-other-4", "Køge", 55.4574, 12.1817, "/", 22),
  makeDemoSession("demo-other-5", "Sønderborg", 54.9094, 9.7917, "/om-os", 150),
  makeDemoSession("demo-other-6", "Fredericia", 55.5657, 9.7530, "/", 70),
  makeDemoSession("demo-other-7", "Skive", 56.5683, 9.0254, "/blog", 55),
];

function CustomerMapPage() {
  const [period, setPeriod] = useState<Period>(30);
  const [marketingOnly, setMarketingOnly] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [demoMode, setDemoMode] = useState(false);
  // Demo sample size — lets us test color coding (5), pre-suppression density
  // (15), the >20 label-suppression threshold (28), and heavy-load behavior (50).
  const [demoCount, setDemoCount] = useState<5 | 15 | 28 | 50>(28);
  const liveFn = useServerFn(getLiveVisitorSessions);
  const topFn = useServerFn(getTopVisitorCities);
  const contactsFn = useServerFn(listMarketingContacts);
  const heatFn = useServerFn(getCityHeatmap);

  const liveQuery = useQuery({
    queryKey: ["customer-map-live"],
    queryFn: () => liveFn({ data: { windowMinutes: 5 } }),
    refetchInterval: 15_000,
  });

  const topQuery = useQuery({
    queryKey: ["customer-map-top", period],
    queryFn: () => topFn({ data: { days: period, limit: 10 } }),
    refetchInterval: 60_000,
  });

  const contactsQuery = useQuery({
    queryKey: ["customer-map-contacts", marketingOnly],
    queryFn: () => contactsFn({ data: { limit: 50, marketingConsentOnly: marketingOnly } }),
    refetchInterval: 60_000,
  });

  const heatQuery = useQuery({
    queryKey: ["customer-map-heatmap-embedded", period],
    queryFn: () => heatFn({ data: { days: period, limit: 50 } }),
    refetchInterval: 5 * 60_000,
  });

  // For ≤28 we slice the curated dataset (preserving the København/Aarhus
  // clusters at the front). For 50 we duplicate with offset coordinates and
  // unique IDs so the cluster-buckets in CustomerLiveMap don't collapse them.
  const demoSessions = useMemo<DemoSession[]>(() => {
    if (demoCount <= DEMO_SESSIONS.length) {
      return DEMO_SESSIONS.slice(0, demoCount);
    }
    const extra: DemoSession[] = [];
    const need = demoCount - DEMO_SESSIONS.length;
    for (let i = 0; i < need; i += 1) {
      const base = DEMO_SESSIONS[i % DEMO_SESSIONS.length];
      // ~1.1 km offset per step so duplicates don't land on the same coord.
      const jitter = (Math.floor(i / DEMO_SESSIONS.length) + 1) * 0.01;
      extra.push({
        ...base,
        session_id: `${base.session_id}-dup-${i}`,
        latitude: base.latitude + jitter,
        longitude: base.longitude + jitter,
      });
    }
    return [...DEMO_SESSIONS, ...extra];
  }, [demoCount]);

  const sessions = demoMode ? demoSessions : (liveQuery.data?.sessions ?? []);
  const heatPoints = heatQuery.data?.points ?? [];

  const liveCount = sessions.length;
  const cities = topQuery.data?.cities ?? [];
  const contacts = contactsQuery.data?.contacts ?? [];
  const totalVisitorsTop = cities.reduce((acc, c) => acc + c.visitors, 0);

  // Aggregate live sessions by path category — mirrors classifyPath() in
  // CustomerLiveMap.tsx so the counts match the dot-colors on the map.
  const categoryCounts = useMemo(() => {
    const counts = { cars: 0, blog: 0, contact: 0, other: 0 };
    for (const s of sessions) {
      const p = s.current_path ?? "";
      if (p.startsWith("/biler-til-salg")) counts.cars += 1;
      else if (p.startsWith("/blog")) counts.blog += 1;
      else if (p.startsWith("/kontakt")) counts.contact += 1;
      else counts.other += 1;
    }
    return counts;
  }, [sessions]);

  const openFullscreen = () => {
    if (typeof window === "undefined") return;
    const features = "noopener,noreferrer,width=1400,height=900";
    window.open("/customer-map-live", "customer-map-fullscreen", features);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Customer Map</h1>
          <p className="text-sm text-muted-foreground">
            Live overblik over besøgende og marketing-leads. Opdaterer hvert 15. sekund.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            {liveCount} aktive nu
            {demoMode ? (
              <span className="ml-1 text-xs text-muted-foreground">(demo)</span>
            ) : null}
          </Badge>
          <div className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1">
            <Sparkles size={14} className="text-accent" />
            <Label htmlFor="demo-toggle" className="cursor-pointer text-xs">
              Demo
            </Label>
            <Switch
              id="demo-toggle"
              checked={demoMode}
              onCheckedChange={setDemoMode}
            />
            {/* Sample-size picker — only meaningful while demo is active.
                Disabled (greyed) otherwise so the control stays visible
                and discoverable without being misleading. */}
            <div className="ml-1 flex items-center gap-0.5 border-l border-border pl-2">
              {([5, 15, 28, 50] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setDemoCount(n)}
                  disabled={!demoMode}
                  className={`rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
                    demoCount === n && demoMode
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              liveQuery.refetch();
              topQuery.refetch();
              contactsQuery.refetch();
              heatQuery.refetch();
            }}
          >
            <RefreshCw size={14} className="mr-1.5" />
            Opdater
          </Button>
          <Button variant="outline" size="sm" onClick={openFullscreen}>
            <ExternalLink size={14} className="mr-1.5" />
            Åbn i nyt vindue
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
          <CardTitle className="flex flex-wrap items-center gap-x-2 gap-y-1 text-base">
            <span className="flex items-center gap-2">
              <MapPin size={16} className="text-accent" />
              Live besøgende (sidste 5 min)
            </span>
            <span className="flex flex-wrap items-center gap-1.5 text-xs font-normal">
              <span className="text-muted-foreground">·</span>
              <span
                className="rounded-full px-2 py-0.5 tabular-nums"
                style={{ background: "hsl(45 80% 50% / 0.15)", color: "hsl(45 80% 35%)" }}
              >
                {categoryCounts.cars} biler
              </span>
              <span
                className="rounded-full px-2 py-0.5 tabular-nums"
                style={{ background: "hsl(217 90% 55% / 0.15)", color: "hsl(217 90% 45%)" }}
              >
                {categoryCounts.blog} blog
              </span>
              <span
                className="rounded-full px-2 py-0.5 tabular-nums"
                style={{ background: "hsl(142 70% 40% / 0.15)", color: "hsl(142 70% 30%)" }}
              >
                {categoryCounts.contact} kontakt
              </span>
              {categoryCounts.other > 0 ? (
                <span className="rounded-full bg-muted px-2 py-0.5 tabular-nums text-muted-foreground">
                  {categoryCounts.other} andet
                </span>
              ) : null}
            </span>
            {heatPoints.length > 0 ? (
              <span className="text-xs font-normal text-muted-foreground">
                · Heat-map: top {heatPoints.length} byer ({period} dage)
              </span>
            ) : null}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            <Tabs
              value={String(period)}
              onValueChange={(v) => setPeriod(Number(v) as Period)}
            >
              <TabsList className="h-8">
                <TabsTrigger value="7" className="px-2.5 text-xs">
                  7d
                </TabsTrigger>
                <TabsTrigger value="30" className="px-2.5 text-xs">
                  30d
                </TabsTrigger>
                <TabsTrigger value="90" className="px-2.5 text-xs">
                  90d
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2 border-l border-border pl-3">
              <Flame size={14} className="text-orange-500" />
              <Label htmlFor="heat-toggle-embedded" className="cursor-pointer text-xs">
                Heat-map
              </Label>
              <Switch
                id="heat-toggle-embedded"
                checked={showHeatmap}
                onCheckedChange={setShowHeatmap}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="relative h-[600px] w-full">
            <LazyCustomerMap
              sessions={sessions}
              heatPoints={heatPoints}
              showHeatmap={showHeatmap}
              heatPeriodLabel={`sidste ${period} dage`}
            />
            {/* Label-suppression notice — kicks in at >20 simultaneous sessions
                (matches threshold in CustomerLiveMap.tsx). Without this badge,
                the missing path-labels look like a bug instead of a deliberate
                anti-clutter measure. */}
            {sessions.length > 20 ? (
              <div className="pointer-events-none absolute left-3 top-3 z-[1000] rounded-md border border-border bg-card/95 px-2.5 py-1.5 text-[11px] shadow-lg backdrop-blur">
                <span className="font-medium text-foreground">{sessions.length} sessioner</span>
                <span className="ml-1.5 text-muted-foreground">— sti-labels skjult</span>
              </div>
            ) : null}
            {/* Path-color legend — matches .cmp-pulse-label--* tokens in styles.css */}
            <div className="pointer-events-none absolute bottom-3 right-3 z-[1000] rounded-lg border border-border bg-card/95 px-3 py-2 text-xs shadow-lg backdrop-blur">
              <p className="mb-1.5 font-medium text-foreground">Sti-farver</p>
              <ul className="space-y-1">
                <li className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: "hsl(45 80% 50%)" }}
                  />
                  <span className="text-muted-foreground">Biler</span>
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: "hsl(217 90% 55%)" }}
                  />
                  <span className="text-muted-foreground">Blog</span>
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: "hsl(142 70% 40%)" }}
                  />
                  <span className="text-muted-foreground">Kontakt</span>
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: "hsl(0 0% 8%)" }}
                  />
                  <span className="text-muted-foreground">Andet</span>
                </li>
              </ul>
            </div>
            {showHeatmap && heatPoints.length > 0 ? (
              <div className="pointer-events-none absolute bottom-3 left-3 z-[1000] rounded-lg border border-border bg-card/95 px-3 py-2 text-xs shadow-lg backdrop-blur">
                <p className="mb-1.5 font-medium text-foreground">Besøgs-tæthed</p>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Lav</span>
                  <div
                    className="h-2 w-24 rounded"
                    style={{
                      background:
                        "linear-gradient(to right, #1e3a8a, #0ea5e9, #facc15, #f97316, #dc2626)",
                    }}
                  />
                  <span className="text-muted-foreground">Høj</span>
                </div>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users size={16} className="text-accent" />
              Top 10 byer
            </CardTitle>
            <Tabs value={String(period)} onValueChange={(v) => setPeriod(Number(v) as Period)}>
              <TabsList className="h-8">
                <TabsTrigger value="7" className="text-xs">7d</TabsTrigger>
                <TabsTrigger value="30" className="text-xs">30d</TabsTrigger>
                <TabsTrigger value="90" className="text-xs">90d</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {cities.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Ingen by-data endnu. Når besøgende accepterer statistik-cookies, dukker de op her.
              </p>
            ) : (
              <ol className="space-y-1.5">
                {cities.map((c, idx) => {
                  const pct = totalVisitorsTop > 0 ? (c.visitors / totalVisitorsTop) * 100 : 0;
                  return (
                    <li
                      key={`${c.city}-${c.country ?? ""}`}
                      className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/60"
                    >
                      <span className="w-5 text-xs font-medium text-muted-foreground">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="truncate text-sm font-medium">
                            {c.city}
                            {c.country ? (
                              <span className="ml-1.5 text-xs text-muted-foreground">
                                {c.country}
                              </span>
                            ) : null}
                          </p>
                          <p className="text-xs tabular-nums text-muted-foreground">
                            {c.visitors} {c.visitors === 1 ? "besøgende" : "besøgende"}
                          </p>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full bg-accent"
                            style={{ width: `${Math.max(pct, 4)}%` }}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Tag size={16} className="text-accent" />
              Marketing-leads
            </CardTitle>
            <Button
              variant={marketingOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setMarketingOnly(!marketingOnly)}
              className="h-8 text-xs"
            >
              {marketingOnly ? "Vis alle" : "Kun marketing-samtykke"}
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {contacts.length === 0 ? (
              <p className="px-6 py-6 text-center text-sm text-muted-foreground">
                Ingen leads endnu.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kilde</TableHead>
                    <TableHead>Kontakt</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead className="text-right">Tid</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {c.source}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        {c.full_name_plain ? (
                          <p className="truncate text-sm font-medium">{c.full_name_plain}</p>
                        ) : null}
                        {c.email_plain ? (
                          <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                            <Mail size={11} /> {c.email_plain}
                          </p>
                        ) : null}
                        {c.phone_plain ? (
                          <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                            <Phone size={11} /> {c.phone_plain}
                          </p>
                        ) : null}
                        {!c.full_name_plain && !c.email_plain && !c.phone_plain ? (
                          <span className="text-xs text-muted-foreground">
                            (kun hashet — intet marketing-samtykke)
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {c.city ?? "—"}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(c.created_at), {
                          addSuffix: true,
                          locale: da,
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
